import { z, EnumLike } from 'zod'
import { formatDateForApi, fromString } from '~/shared/lib/date'
import { sanitizePhoneNumber, sanitizePriceNumber } from '~/shared/lib/utils'

export const dateSchema = z
  .preprocess((arg) => {
    if (typeof arg == 'string') {
      return fromString(arg)
    }
    return arg
  }, z.date())
  .transform(formatDateForApi)

export const phoneScheme = z
  .string()
  .superRefine((value, ctx) => {
    const sanitized = sanitizePhoneNumber(value)
    if (sanitized.length > 0 && sanitized.length < 12) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Неверный номер телефона',
      })
    }
  })
  .transform(sanitizePhoneNumber)

export const priceRequiredScheme = z
  .string()
  .or(z.number())
  .superRefine((value, ctx) => {
    const sanitized = sanitizePriceNumber(value)
    if (!sanitized) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Обязательное поле',
      })
    }
    if (sanitized > 999999999) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Превышена допустимая стоимость',
      })
    }
  })
  .transform(sanitizePriceNumber)

export const priceRequiredWithZeroScheme = z
  .string()
  .or(z.number())
  .superRefine((value, ctx) => {
    const sanitized = sanitizePriceNumber(value)
    if (!sanitized && sanitized !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Обязательное поле',
      })
    }
    if (sanitized > 999999999) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Превышена допустимая стоимость',
      })
    }
  })
  .transform(sanitizePriceNumber)

export const optionSchema = z.object({
  id: z.string(),
  label: z.string(),
})
export const uuidOptionSchema = z
  .union([z.string().uuid(), optionSchema])
  .transform((arg) => {
    if (typeof arg === 'string') {
      return arg
    }
    return arg.id
  })
export const uuidOptionsSchema = z.array(uuidOptionSchema)

export const enumOptionSchema = <T extends EnumLike>(values: T) =>
  z.union([z.nativeEnum(values), optionSchema]).transform((arg) => {
    if (typeof arg === 'object' && 'id' in arg) {
      return arg.id
    }
    return arg
  })

export const yearSchema = z
  .union([
    z
      .string()
      .regex(
        /^\d{4}$/,
        'Год имеет неправильный формат, ожидается, например "2022"',
      ),
    z.number(),
  ])
  .transform((arg) => {
    if (typeof arg === 'string') return parseInt(arg)
    return arg
  })
const yearsSeparatedByCommasRegex = /\b\d{4}\b(?:,\s*\b\d{4}\b)*/g
export const yearsSeparatedByCommasSchema = (min = 0) =>
  z
    .union([
      z
        .string()
        .regex(
          yearsSeparatedByCommasRegex,
          'Года имеют неправильный формат, ожидается, например "1999,2004,2022"',
        ),
      z.array(yearSchema).min(min),
    ])
    .transform((arg) => {
      if (typeof arg === 'string') {
        return arg.match(/\d{4}/g)?.map(Number)
      }
      return arg
    })

export const numberScheme = z.coerce.number()

export const numberRequiredWithZeroScheme = z
  .string()
  .or(z.number())
  .superRefine((value, ctx) => {
    const number = value ? Number(value) : ''
    if (!number && number !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Обязательное поле',
      })
    }
  })
  .transform(Number)

export const numberRequiredScheme = z.coerce
  .number({
    invalid_type_error: 'Обязательное поле',
  })
  .min(0.01, 'Обязательное поле')

export const stringRequiredScheme = z
  .string()
  .trim()
  .min(1, 'Обязательное поле')

export const okpoRequiredSchema = z
  .string()
  .trim()
  .regex(/^\d{8}$|^\d{10}$/, 'Только цифры, длина ОКПО - 8 или 10 символов')

export const innRequiredSchema = z
  .string()
  .trim()
  .min(1, 'Обязательное поле')
  .regex(/^\d{10}$|^\d{12}$/, 'Только цифры, длина ИНН - 10 или 12 символов')

export const kppRequiredSchema = z
  .string()
  .trim()
  .min(1, 'Обязательное поле')
  .refine(
    (value) => /^\d{9}$/.test(value),
    'Только цифры, длина КПП - 9 символов',
  )

export const accountNumberRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{20}$/.test(value),
    'Только цифры, длина Расчётного счёта - 20 символов',
  )

export const corrAccountNumberRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{20}$/.test(value),
    'Только цифры, длина Корреспондентского счёта - 20 символов',
  )

export const bikRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{9}$/.test(value),
    'Только цифры, длина БИК - 9 символов',
  )

export const ogrnRequiredSchema = z
  .string()
  .trim()
  .min(1, 'Обязательное поле')
  .refine(
    (value) => /^(\d{13}|\d{15})$/.test(value),
    'Только цифры, длина ОГРН - 13 символов, ОГРНИП - 15 символов',
  )

export const uinRequiredSchema = z
  .string()
  .trim()
  .min(1, 'Обязательное поле')
  .refine(
    (value) => /^\d{20}$|^\d{25}$/.test(value),
    'Только цифры, длина УИН - 20 или 25 символов',
  )

export const vinRequiredSchema = z
  .string()
  .trim()
  .min(1, 'Обязательное поле')
  .refine(
    (value) => /^[0-9A-Z]{17}$/.test(value.toUpperCase()),
    'Длина VIN - 17 символов, только цифры и латинские буквы',
  )
  .transform((value) => value.toUpperCase())

export const kbkRequiredSchema = z
  .string()
  .trim()
  .min(1, 'Обязательное поле')
  .refine(
    (value) => /^\d{20}$/.test(value),
    'Только цифры, длина КБК - 20 символов',
  )

export const oktmoRequiredSchema = z
  .string()
  .trim()
  .min(1, 'Обязательное поле')
  .refine(
    (value) => /^\d{8}$|^\d{11}$/.test(value),
    'Только цифры, длина ОКТМО - 8 или 11 символов',
  )
